import React from 'react';
import { Stack } from '@mui/material';
import { CustomerVisibility } from 'modules/dealSheet';
import { useFormContext, useWatch } from 'react-hook-form';
import { ChargeBucketRef } from 'modules/equipment/types';
import { blue, grey } from '@mui/material/colors';
import { ListingForm } from 'components/shared/TableListing/types';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IndicatorMenu, IndicatorMenuOption } from 'components/shared/IndicatorMenu';

import { RollUpSelect } from './RollUpSelect';
import type { ChargeListingService } from '../../../../../lib';
import { QuoteChargeListingEntity } from '../../../../../types';

interface Props {
  rowIndex: number;
  scope: ChargeBucketRef;
  chargeService: ChargeListingService;
}

const VISIBILITY_OPTIONS: IndicatorMenuOption<CustomerVisibility>[] = [
  {
    color: grey[500],
    label: 'Visible to customer',
    value: CustomerVisibility.Visible,
    renderIcon: (color: string) => <Visibility sx={{ color, fontSize: '1rem' }} />,
  },
  {
    color: blue[500],
    label: 'Internal only',
    value: CustomerVisibility.InternalOnly,
    renderIcon: (color: string) => <VisibilityOff sx={{ color, fontSize: '1rem' }} />,
  },
];

export const LineItemVisibility = ({ rowIndex, scope, chargeService }: Props) => {
  const { control, setValue } = useFormContext<ListingForm<QuoteChargeListingEntity>>();

  const chargeId = useWatch({ control, name: `bodyFields.${rowIndex}.charge.id` });
  const customerVisibility = useWatch({
    control,
    name: `bodyFields.${rowIndex}.charge.customerVisibility`,
  });

  const handleVisibilityChange = (next: CustomerVisibility) => {
    setValue(`bodyFields.${rowIndex}.charge.customerVisibility`, next, { shouldValidate: true });

    if (next !== CustomerVisibility.InternalOnly) {
      setValue(`bodyFields.${rowIndex}.charge.parentId`, null, { shouldValidate: true });
      setValue(`bodyFields.${rowIndex}.charge.parentType`, undefined, { shouldValidate: true });
    }
  };

  if (!chargeId) return null;

  const isInternalOnly = customerVisibility === CustomerVisibility.InternalOnly;

  return (
    <Stack gap="0.5rem">
      <IndicatorMenu
        options={VISIBILITY_OPTIONS}
        onChange={handleVisibilityChange}
        value={customerVisibility ?? CustomerVisibility.Visible}
      />

      {isInternalOnly && <RollUpSelect rowIndex={rowIndex} scope={scope} chargeService={chargeService} />}
    </Stack>
  );
};
